import React, { useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useConfigStore, useAppStore } from '../../stores';
import DetailTable from '../Detail/DetailTable';
import { triggerPricing } from '../../utils/DrupalEntityTypes/FloorPlan/FloorPlanHelpers';
import { createSkeletonItem } from '../Skeleton/SkeletonHelpers';

/**
 * Creates a fee cell object for the table
 * @param {string} mobileLabel - Label for mobile display
 * @param {string} fee - Primary fee string (e.g., "$10,000")
 * @param {string} secondFee - Secondary fee string (optional)
 * @returns {Object} Fee cell object
 */
const createFeeCell = (mobileLabel, fee, secondFee) => ({
  mobileLabel,
  data: fee || null,
  note: secondFee ? `Second Person: ${secondFee}` : null,
});

/**
 * FloorPlanFinancialPlans component displays fee information in a table format.
 * Fee data is pre-processed by Drupal with formatted prices.
 *
 * @param {Object} props - Component props
 * @param {Object} props.feeData - Fee data containing planMap with pre-formatted prices
 * @param {string} props.className - Additional CSS class names
 * @returns {JSX.Element|null} The rendered component or null if no data
 */
const FloorPlanFinancialPlans = ({ feeData, feeCount, className }) => {
  const [tableHeadData, setTableHeadData] = useState(null);
  const [tableRowData, setTableRowData] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showPricingInitially, setShowPricingInitially] = useState(false);

  // planMap and hasFeePlanNames are pre-processed by Drupal
  const { planMap, hasFeePlanNames } = feeData || {};

  // Know if we're loading: we have a fee count but no actual planMap yet
  const isLoading = feeCount && !planMap;

  // IMPORTANT: All hooks must be called before any conditional returns
  const featureFlags = useConfigStore((state) => state.featureFlags);
  const pricingTriggered = useAppStore((state) => state.pricingTriggered);
  const setPricingTriggered = useAppStore((state) => state.setPricingTriggered);

  const { platformShowPricingInitially, platformShowPricing } = featureFlags || {};

  useEffect(() => {
    setShowPricingInitially(platformShowPricingInitially);
  }, [platformShowPricingInitially]);

  // Determine which fee columns to display based on pre-processed planMap
  const { anyPlanHasEntranceFee, anyPlanHasMonthlyFee } = useMemo(() => {
    if (!planMap || Object.keys(planMap).length === 0) {
      return { anyPlanHasEntranceFee: false, anyPlanHasMonthlyFee: false };
    }
    const plans = Object.values(planMap);
    return {
      anyPlanHasEntranceFee: plans.some((plan) => plan.entranceFee),
      anyPlanHasMonthlyFee: plans.some((plan) => plan.monthlyFee),
    };
  }, [planMap]);

  // Initialize pricing from localStorage on mount
  useEffect(() => {
    const initializePricing = () => {
      try {
        const displayedFees = JSON.parse(localStorage.getItem('userDisplayedFees'));
        if (displayedFees) {
          triggerPricing(setPricingTriggered);
        }
      } catch (error) {
        console.warn('Failed to parse userDisplayedFees from localStorage:', error);
      }
    };

    initializePricing();
  }, [setPricingTriggered]);

  // Generate table row data from pre-processed planMap
  const generateRowData = useCallback(() => {
    if (!planMap) return [];

    // Sort plans by weight, then by name
    const sortedPlans = Object.values(planMap).sort((a, b) => {
      // Sort by weight first
      if (a.planWeight !== b.planWeight) {
        return (a.planWeight || 0) - (b.planWeight || 0);
      }
      // If weights are equal, sort alphabetically by name
      return (a.planName || '').localeCompare(b.planName || '');
    });

    return sortedPlans.reduce((rowData, plan) => {
      const planCells = [];

      // Add entrance fee cell if applicable
      if (anyPlanHasEntranceFee) {
        planCells.push(createFeeCell(
          'Entrance Fee:',
          plan.entranceFee,
          plan.entranceSecondFee,
        ));
      }

      // Add monthly fee cell if applicable
      if (anyPlanHasMonthlyFee) {
        planCells.push(createFeeCell(
          'Monthly Fee:',
          plan.monthlyFee,
          plan.monthlySecondFee,
        ));
      }

      // Only add row if it has cells
      if (planCells.length > 0) {
        const planData = {
          key: plan.planId || plan.planName || `plan-${rowData.length}`,
          cells: planCells,
        };

        // Add plan name if applicable (hasFeePlanNames is pre-calculated by Drupal)
        if (plan.planName && plan.planName.trim() && hasFeePlanNames) {
          planData.label = plan.planName;
        }

        rowData.push(planData);
      }

      return rowData;
    }, []);
  }, [planMap, hasFeePlanNames, anyPlanHasEntranceFee, anyPlanHasMonthlyFee]);

  // Generate skeleton rows when loading
  // Note: We create 3 generic skeleton rows since we don't know the exact count of PLANS (only fee groups)
  const generateSkeletonRows = useCallback((fadeOut = false) => {
    if (!feeCount) return [];

    const skeletonRows = [];
    // Create 3 skeleton rows as a reasonable default for pricing tables
    const skeletonCount = 3;

    for (let i = 0; i < skeletonCount; i++) {
      const cells = [];

      // Entrance fee cell skeleton
      cells.push({
        key: `skeleton-cell-${i}-entrance`,
        mobileLabel: createSkeletonItem('div', 'detailTable__itemLabel', 'full', true, i, fadeOut),
        data: createSkeletonItem('div', 'detailTable__itemData', 'full', true, i, fadeOut),
        note: null,
      });

      // Monthly fee cell skeleton
      cells.push({
        key: `skeleton-cell-${i}-monthly`,
        mobileLabel: createSkeletonItem('div', 'detailTable__itemLabel', 'full', true, i, fadeOut),
        data: createSkeletonItem('div', 'detailTable__itemData', 'full', true, i, fadeOut),
        note: null,
      });

      skeletonRows.push({
        key: `skeleton-row-${i}`, // Add unique key for each skeleton row
        label: createSkeletonItem('div', 'detailTable__itemLabel', 'full', true, i, fadeOut),
        cells,
      });
    }

    return skeletonRows;
  }, [feeCount]);

  // Generate skeleton header (assume 3 columns: plan name, entrance fee, monthly fee)
  const generateSkeletonHeader = useCallback((fadeOut = false) => {
    return [
      createSkeletonItem('div', 'detailTable__item', 'full', true, 0, fadeOut),
      createSkeletonItem('div', 'detailTable__item', 'full', true, 1, fadeOut),
      createSkeletonItem('div', 'detailTable__item', 'full', true, 2, fadeOut),
    ];
  }, []);

  // Generate real header based on actual data
  const generateRealHeader = useCallback(() => {
    const headData = [];

    if (hasFeePlanNames) {
      headData.push(''); // Placeholder for plan name column
    }

    if (anyPlanHasEntranceFee) {
      headData.push('Entrance Fee');
    }

    if (anyPlanHasMonthlyFee) {
      headData.push('Monthly Fee');
    }

    return headData;
  }, [hasFeePlanNames, anyPlanHasEntranceFee, anyPlanHasMonthlyFee]);

  // Update table row data and header when dependencies change
  useEffect(() => {
    if (isLoading) {
      // Show skeletons while loading
      const skeletonRows = generateSkeletonRows(false);
      const skeletonHeaders = generateSkeletonHeader(false);
      setTableRowData(skeletonRows);
      setTableHeadData(skeletonHeaders);
      setIsTransitioning(false);
    } else if (planMap) {
      // Data loaded - show real data
      const realRows = generateRowData();
      const realHeaders = generateRealHeader();
      setTableRowData(realRows);
      setTableHeadData(realHeaders);
      setIsTransitioning(false);
    }
    // Don't include tableRowData in dependencies to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, planMap]);

  // Don't render if pricing is not enabled
  if (!platformShowPricing) {
    return null;
  }

  // Don't render if there's no pricing data and not loading
  if (!isLoading && !tableRowData?.length) {
    return null;
  }

  return (
    <DetailTable
      tableHeadData={isLoading || (tableHeadData && tableHeadData.length > 0) ? tableHeadData : null}
      tableRowData={tableRowData}
      className={classNames( {
        [className]: className,
        'detailSection--show': pricingTriggered,
        'detailSection--toggleable': !showPricingInitially,
        'content-fadeIn': !isLoading && !isTransitioning && tableRowData?.length > 0,
      })}
    />
  );
};

FloorPlanFinancialPlans.propTypes = {
  feeCount: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  // feeData is pre-processed by Drupal with formatted price strings
  feeData: PropTypes.shape({
    hasFees: PropTypes.bool,
    hasFeePlanNames: PropTypes.bool,
    planMap: PropTypes.objectOf(
      PropTypes.shape({
        planId: PropTypes.string,
        planName: PropTypes.string,
        planWeight: PropTypes.number,
        entranceFee: PropTypes.string,
        entranceSecondFee: PropTypes.string,
        monthlyFee: PropTypes.string,
        monthlySecondFee: PropTypes.string,
      })
    ),
  }),
  className: PropTypes.string,
};

FloorPlanFinancialPlans.defaultProps = {
  feeCount: null,
  feeData: null,
  className: null,
};

export default FloorPlanFinancialPlans;
